import { AuthPageWrapper } from '../Wrapper/authPageWrapper/AuthPageWrapper';
import { PasswordStrengthMeter } from './passwordStrengthMeter/PasswordStrengthMeter';
import SusyButton from '../../shared/buttons/SusyButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/index';
import { useState } from 'react';
import { getPasswordStrength } from '../../../utils/utils';
import { toast } from 'react-toastify';
import { unauthenticatedPaths } from '../../../router/constants/route-paths';
import { useNavigate } from 'react-router-dom';
import SusyFormField from '../../shared/forms/SusyFormField/SusyFormField';

const REQUIRED_PASSWORD_STRENGTH = 5;

interface IProps {
    onSubmit: (
        password: string,
        repeatedPassword?: string,
        oldPassword?: string
    ) => Promise<void>;
    buttonText: string;
    title?: string;
    subtitle?: string;
    oldPasswordRequired?: boolean;
    wideForm?: boolean;
}

export const FormWithPasswordMeter = ({
    onSubmit,
    buttonText,
    title,
    subtitle,
    oldPasswordRequired,
    wideForm,
}: IProps) => {
    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [dirty, setIsDirty] = useState<boolean>(false);

    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.authentication
                .formWithPasswordMeter
    );

    const navigate = useNavigate();

    const onClickHandler = async () => {
        if (getPasswordStrength(password) < REQUIRED_PASSWORD_STRENGTH) {
            toast.warning(localization.requirementsError);
            return;
        } else if (password !== confirmPassword) {
            toast.warning(localization.noMatch);
            return;
        }
        await onSubmit(password, confirmPassword, oldPassword);
        navigate(unauthenticatedPaths.signIn);
    };

    return (
        <div data-testid={'form-with-password-meter'}>
            <AuthPageWrapper
                title={title ?? localization.headline}
                subtitle={subtitle}
                wideForm={wideForm}
            >
                <>
                    {oldPasswordRequired && (
                        <SusyFormField
                            required
                            label={{
                                fieldIdentifier: 'oldPassword',
                                text: localization.oldPassword,
                            }}
                        >
                            <input
                                data-testid={'old-password-input'}
                                autoComplete={'off'}
                                className='form-control mb-2'
                                type='password'
                                value={oldPassword}
                                onChange={(e) => {
                                    setOldPassword(e.target.value);
                                }}
                            />
                        </SusyFormField>
                    )}
                    <SusyFormField
                        required
                        label={{
                            fieldIdentifier: 'password',
                            text: localization.newPassword,
                        }}
                    >
                        <input
                            data-testid={'new-password-input'}
                            autoComplete={'off'}
                            className='form-control mb-2'
                            type='password'
                            value={password}
                            onChange={(e) => {
                                setIsDirty(true);
                                setPassword(e.target.value);
                            }}
                        />
                        <PasswordStrengthMeter
                            password={password}
                            dirty={dirty}
                        ></PasswordStrengthMeter>
                    </SusyFormField>
                    <SusyFormField
                        required
                        label={{
                            fieldIdentifier: 'confirmPassword',
                            text: localization.confirmNewPassword,
                        }}
                    >
                        <input
                            data-testid={'confirm-new-password-input'}
                            autoComplete={'off'}
                            className='form-control mb-4'
                            type='password'
                            value={confirmPassword}
                            onChange={(e) => {
                                setIsDirty(true);
                                setConfirmPassword(e.target.value);
                            }}
                        />
                    </SusyFormField>
                    <SusyButton
                        text={buttonText}
                        onClickHandler={onClickHandler}
                        classes='mt-2'
                        fullWidth
                    />
                </>
            </AuthPageWrapper>
        </div>
    );
};
